import React from "react";
import {
  Shield,
  Clock,
  Mail,
  Download,
  Trash2,
  AlertTriangle,
  BarChart3,
  ChevronRight,
  Eye,
  ExternalLink,
  Settings,
  ArrowLeft,
  Calendar,
  FileText,
  HelpCircle,
  Users,
  Megaphone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MarketingConsentCard } from "@core/components/MarketingConsentCard";
import { Feature } from "@core/components/Feature";
import { useToast } from "@core/hooks/use-toast";
import { logError } from "@core/lib/error-handling";
import { useDataControl } from "@core/hooks/useDataControl";
import { type ProductType } from "@core/config/products.config";
import DeleteAccountModal from "@core/components/account/DeleteAccountModal";

interface DataControlUIProps {
  productType: ProductType;
  settings: any;
  filteredData: any;
  upcomingDeletions: any[];
  showPreview: boolean;
  showExportModal: boolean;
  showDeleteModal?: boolean;
  exportFormat: "json" | "csv" | "pdf" | "markdown";
  hasEmailAccount: boolean;
  config: any;
  setSettings: (settings: any) => void;
  setShowPreview: (show: boolean) => void;
  setShowExportModal: (show: boolean) => void;
  setShowDeleteModal?: (show: boolean) => void;
  setExportFormat: (format: "json" | "csv" | "pdf" | "markdown") => void;
  handleSettingChange: (key: string, value: string | boolean) => void;
  handleExportData: (format: "json" | "csv" | "pdf" | "markdown") => void;
  handleDeleteAccount: () => void;
  postponeDeletion: (type: string) => void;
  copyDpoEmail: () => void;
}

export const DataControlUI: React.FC<DataControlUIProps> = ({
  productType,
  settings,
  filteredData,
  upcomingDeletions,
  showPreview,
  showExportModal,
  showDeleteModal = false,
  exportFormat,
  hasEmailAccount,
  config,
  setSettings,
  setShowPreview,
  setShowExportModal,
  setShowDeleteModal,
  setExportFormat,
  handleSettingChange,
  handleExportData,
  handleDeleteAccount,
  postponeDeletion,
  copyDpoEmail,
}) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("polls");

  // Détecter le produit d'origine pour le retour
  const getOriginProduct = () => {
    const referrer = document.referrer;
    const currentPath = window.location.pathname;

    // Détecter depuis le referrer ou le path courant
    if (
      referrer.includes("localhost:3005") ||
      currentPath.startsWith("/date")
    ) {
      return { name: "Date Polls", path: "/date", icon: Calendar };
    }
    if (
      referrer.includes("localhost:3002") ||
      currentPath.startsWith("/form")
    ) {
      return { name: "Form Polls", path: "/form", icon: FileText };
    }
    if (
      referrer.includes("localhost:3006") ||
      currentPath.startsWith("/quizz")
    ) {
      return { name: "Quizz", path: "/quizz", icon: HelpCircle };
    }
    if (
      referrer.includes("localhost:3003") ||
      currentPath.startsWith("/availability")
    ) {
      return { name: "Availability", path: "/availability", icon: Users };
    }

    return { name: "Accueil", path: "/", icon: ArrowLeft };
  };

  const originProduct = getOriginProduct();
  const privacyPath =
    originProduct.path === "/" ? "/privacy" : `${originProduct.path}/privacy`;

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="pt-4">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header puissant */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary rounded-full mb-6">
              <Shield className="w-10 h-10 text-primary-foreground" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t("dataControl.title")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("dataControl.subtitle")}
              <span className="font-semibold text-primary">
                {" "}
                {t("dataControl.subtitleHighlight")}
              </span>
            </p>
          </div>

          {/* Alertes suppressions à venir */}
          {upcomingDeletions.length > 0 && (
            <div className="mb-8 space-y-4">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-warning" />
                {t("dataControl.upcomingDeletions.heading")}
              </h2>
              {upcomingDeletions.map((warning, index) => (
                <div
                  key={index}
                  className="bg-warning/10 border border-warning/30 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-warning" />
                        <h3 className="font-semibold text-warning">
                          {warning.type === "chat"
                            ? filteredData.labels.conversationName
                            : filteredData.labels.itemPlural}
                        </h3>
                        <span className="px-2 py-1 bg-warning/20 text-warning text-xs font-medium rounded">
                          {t("dataControl.upcomingDeletions.daysBadge", {
                            count: warning.daysUntilDeletion,
                          })}
                        </span>
                      </div>
                      <p className="text-warning mb-3">
                        {warning.itemCount}{" "}
                        {warning.type === "chat"
                          ? filteredData.labels.conversationName
                          : filteredData.labels.itemPlural}{" "}
                        {t("dataControl.upcomingDeletions.willBeDeletedOn", {
                          date: warning.deletionDate.toLocaleDateString(
                            i18n.language || "fr",
                          ),
                        })}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-warning">
                        <span>
                          {hasEmailAccount
                            ? t(
                                "dataControl.upcomingDeletions.emailAlertPlanned",
                              )
                            : t("dataControl.upcomingDeletions.noEmailAlert")}
                        </span>
                        <button
                          onClick={() => postponeDeletion(warning.type)}
                          className="text-warning hover:text-warning/80 font-medium underline"
                          data-testid="data-control-postpone"
                        >
                          {t("dataControl.upcomingDeletions.postpone")}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPreview(true)}
                      className="px-4 py-2 bg-warning text-warning-foreground rounded-lg hover:bg-warning/90 transition-colors text-sm font-medium"
                      data-testid="data-control-preview"
                    >
                      {t("dataControl.upcomingDeletions.viewData")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contrôle des données */}
          <div className="bg-card rounded-xl shadow-sm border border-border mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <Settings className="w-6 h-6 text-primary" />
                {t("dataControl.settings.heading")}
              </h2>

              <div className="space-y-8">
                {/* Conservation conversations IA */}
                <div>
                  <label className="block text-lg font-semibold text-foreground mb-3">
                    {filteredData.labels.conversationPlural}
                  </label>
                  <select
                    value={settings.chatRetention}
                    onChange={(e) =>
                      handleSettingChange("chatRetention", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-muted border border-border text-foreground rounded-lg focus:ring-2 focus:ring-ring focus:border-ring text-base"
                  >
                    <option value="30-days">
                      {t("dataControl.chatRetention.option30Days")}
                    </option>
                    <option value="12-months">
                      {t("dataControl.chatRetention.option12Months")}
                    </option>
                  </select>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t("dataControl.chatRetention.hint")}
                  </p>
                </div>

                {/* Conservation sondages */}
                <div>
                  <label className="block text-lg font-semibold text-foreground mb-3">
                    {filteredData.labels.itemPlural}
                  </label>
                  <select
                    value={settings.pollRetention}
                    onChange={(e) =>
                      handleSettingChange("pollRetention", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-muted border border-border text-foreground rounded-lg focus:ring-2 focus:ring-ring focus:border-ring text-base"
                  >
                    <option value="unlimited">
                      {t("dataControl.pollRetention.optionUnlimited")}
                    </option>
                    <option value="12-months">
                      {t("dataControl.pollRetention.option12Months")}
                    </option>
                    <option value="24-months">
                      {t("dataControl.pollRetention.option24Months")}
                    </option>
                  </select>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t("dataControl.pollRetention.hint")}
                  </p>
                </div>

                {/* Suppression automatique */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {t("dataControl.autoDelete.title")}
                    </h3>
                    <p className="text-muted-foreground">
                      {t("dataControl.autoDelete.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoDeleteEnabled}
                      onChange={(e) =>
                        handleSettingChange(
                          "autoDeleteEnabled",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-input after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Amélioration produit */}
                <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-primary mb-1">
                      {t("dataControl.improvement.title")}
                    </h3>
                    <p className="text-primary">
                      {t("dataControl.improvement.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowDataForImprovement}
                      onChange={(e) =>
                        handleSettingChange(
                          "allowDataForImprovement",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-input after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Notifications email */}
                <div className="flex items-center justify-between p-4 bg-success/10 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-success mb-1 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t("dataControl.emailAlerts.title")}
                    </h3>
                    <p className="text-success">
                      {t("dataControl.emailAlerts.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) =>
                        handleSettingChange(
                          "emailNotifications",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-success/50 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-input after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-success"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Sollicitations commerciales — le seul consentement e-mail
              globalement révocable (décision du 15/09/2026). Section à part
              (pas un toggle parmi d'autres) : c'est cette carte que vise le
              lien /data-control des pieds de page d'e-mails, elle doit être
              trouvable d'un coup d'œil. */}
          <div className="bg-card rounded-xl shadow-sm border border-border mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <Megaphone className="w-6 h-6 text-primary" />
                {t("dataControl.marketing.title")}
              </h2>
              <MarketingConsentCard />
            </div>
          </div>

          {/* Actions rapides */}
          <div className="bg-card rounded-xl shadow-sm border border-border mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-primary" />
                {t("dataControl.quickActions.heading")}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center justify-between p-6 bg-success/10 rounded-lg hover:bg-success/20 transition-colors group"
                  data-testid="export-data-button"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                      <Download className="w-6 h-6 text-success" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-foreground group-hover:text-success">
                        {t("dataControl.quickActions.export.title")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("dataControl.quickActions.export.description")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-success" />
                </button>

                <button
                  onClick={handleDeleteAccount}
                  className="flex items-center justify-between p-6 bg-destructive/10 rounded-lg hover:bg-destructive/20 transition-colors group"
                  data-testid="delete-account-button"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-destructive/20 rounded-full flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-destructive" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-destructive group-hover:text-destructive/80">
                        {t("dataControl.quickActions.delete.title")}
                      </p>
                      <p className="text-sm text-destructive">
                        {t("dataControl.quickActions.delete.description")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-destructive group-hover:text-destructive/80" />
                </button>
              </div>
            </div>
          </div>

          {/* Documentation et support */}
          <div className="bg-primary/10 rounded-xl p-8 border border-primary/30">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Eye className="w-6 h-6 text-primary" />
              {t("dataControl.transparency.heading")}
            </h2>

            <div className="grid md:grid-cols-1 gap-4">
              <Link
                to={privacyPath}
                className="flex items-center justify-between p-4 bg-card rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium text-foreground">
                      {t("dataControl.transparency.privacyPolicy.title")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("dataControl.transparency.privacyPolicy.description")}
                    </p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Link>
            </div>

            {/* Contact DPO */}
            <div className="mt-6 p-4 bg-card rounded-lg border border-primary/30">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-primary">
                    {t("dataControl.dpo.title")}
                  </p>
                  <p className="text-sm text-primary">
                    {t("dataControl.dpo.question")}{" "}
                    <button
                      onClick={copyDpoEmail}
                      className="underline hover:text-foreground"
                    >
                      privacy@doodates.com
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-foreground mb-4">
              {t("dataControl.exportModal.title")}
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => handleExportData("json")}
                className="w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground text-left"
              >
                <div className="font-medium">JSON</div>
                <div className="text-sm text-muted-foreground">
                  {t("dataControl.exportModal.json")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("csv")}
                className="w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground text-left"
              >
                <div className="font-medium">CSV</div>
                <div className="text-sm text-muted-foreground">
                  {t("dataControl.exportModal.csv")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("pdf")}
                className="w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground text-left"
              >
                <div className="font-medium">PDF</div>
                <div className="text-sm text-muted-foreground">
                  {t("dataControl.exportModal.pdf")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("markdown")}
                className="w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground text-left"
              >
                <div className="font-medium">Markdown</div>
                <div className="text-sm text-muted-foreground">
                  {t("dataControl.exportModal.markdown")}
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowExportModal(false)}
              className="mt-4 w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground"
            >
              {t("dataControl.exportModal.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Modal "Voir les données" — le state showPreview existait déjà (bouton
          data-control-preview) mais rien ne le rendait, le clic n'avait aucun
          effet visible. Liste les polls + conversations du produit courant. */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-foreground mb-4">
              {t("dataControl.previewModal.title")}
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t("dataControl.previewModal.pollsHeading", {
                    label: filteredData.labels.itemPlural,
                  })}
                </h4>
                {filteredData.polls.length > 0 ? (
                  <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                    {filteredData.polls.map((poll: any) => (
                      <li key={poll.id}>{poll.title}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("dataControl.previewModal.empty")}
                  </p>
                )}
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t("dataControl.previewModal.conversationsHeading", {
                    label: filteredData.labels.conversationPlural,
                  })}
                </h4>
                {filteredData.conversations.length > 0 ? (
                  <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                    {filteredData.conversations.map((conv: any) => (
                      <li key={conv.id}>{conv.title}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("dataControl.previewModal.empty")}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowPreview(false)}
              className="mt-6 w-full p-3 bg-muted hover:bg-accent rounded-lg text-foreground"
              data-testid="data-control-preview-close"
            >
              {t("dataControl.previewModal.close")}
            </button>
          </div>
        </div>
      )}

      {/* Modal de suppression de compte (double confirmation) */}
      {setShowDeleteModal && (
        <DeleteAccountModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default DataControlUI;
